import {
  AddCircleOutlineRounded,
  AppsOutlined,
  BoltRounded,
  CheckRounded,
  PersonOutline,
  QueryStatsOutlined,
} from "@mui/icons-material";
import { Box, Container, Stack, Typography } from "@mui/material";
import BackgroundImg from "assets/images/auth-background.png";
import BgImg1 from "assets/images/auth-img-1.png";
import BgImg2 from "assets/images/auth-img-2.svg";
import LogoImg from "assets/images/logo.svg";
import PublicThemeProvider from "components/auth/PublicThemeProvider";
import ROUTES, { isSplitAuthRoute } from "constants/routes";
import { FC, ReactNode } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

type AuthContainerProps = {
  children: ReactNode;
};

type SplitContent = {
  title: string;
  description: string;
  features: { icon: ReactNode; text: string }[];
  showMetrics: boolean;
};

const defaultFeatures = [
  {
    icon: <PersonOutline sx={{ fontSize: 24 }} />,
    text: "Цифровой двойник вашего методолога",
  },
  {
    icon: <AppsOutlined sx={{ fontSize: 24 }} />,
    text: "6 форматов: тест, кейс, аудио, изображение",
  },
  {
    icon: <QueryStatsOutlined sx={{ fontSize: 24 }} />,
    text: "Аналитика «до / после» для защиты бюджета",
  },
];

const companyRegisterFeatures = [
  {
    icon: <AddCircleOutlineRounded sx={{ fontSize: 24 }} />,
    text: "300 токенов на старте",
  },
  {
    icon: <CheckRounded sx={{ fontSize: 24 }} />,
    text: "Все функции доступны сразу",
  },
  {
    icon: <BoltRounded sx={{ fontSize: 24 }} />,
    text: "Без звонка отдела продаж",
  },
];

const getSplitContent = (pathname: string): SplitContent => {
  const normalizedPath = pathname.replace(/^\/+/, "");

  if (normalizedPath === ROUTES.COMPANY_REGISTER) {
    return {
      title: "Подключите компанию за пару минут",
      description: [
        "Оценка компетенций в масштабе — без раздувания штата методистов.",
        "Заполните реквизиты и начните сразу, на демо-тарифе.",
      ].join(" "),
      features: companyRegisterFeatures,
      showMetrics: false,
    };
  }

  return {
    title: ["Оценивайте компетенции в масштабе", "без раздувания штата"].join(" — "),
    description: [
      "AI-слой воспроизводит суждение вашего эксперта на каждой работе:",
      "единый эталон, объективный скоринг и цифры эффекта для бизнеса.",
    ].join(" "),
    features: defaultFeatures,
    showMetrics: true,
  };
};

const AuthTabs = ({ pathname }: { pathname: string }) => {
  const normalizedPath = pathname.replace(/^\/+/, "");
  const isLoginRoute = normalizedPath === ROUTES.LOGIN;
  const isRegisterRoute = normalizedPath === ROUTES.USER_REGISTER;

  return (
    <Stack
      direction='row'
      spacing={4}
      sx={{
        pb: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}>
      <Typography
        component={RouterLink}
        to={`/${ROUTES.LOGIN}`}
        sx={{
          textDecoration: "none",
          color: isLoginRoute ? "primary.main" : "text.disabled",
          fontSize: 20,
          fontWeight: 600,
          lineHeight: 1.2,
          pb: 1,
          borderBottom: "3px solid",
          borderColor: isLoginRoute ? "primary.main" : "transparent",
        }}>
        Вход
      </Typography>

      <Typography
        component={RouterLink}
        to={`/${ROUTES.USER_REGISTER}`}
        sx={{
          textDecoration: "none",
          color: isRegisterRoute ? "primary.main" : "text.disabled",
          fontSize: 20,
          fontWeight: 600,
          lineHeight: 1.2,
          pb: 1,
          borderBottom: "3px solid",
          borderColor: isRegisterRoute ? "primary.main" : "transparent",
        }}>
        Регистрация
      </Typography>
    </Stack>
  );
};

const AuthSplitContainer = ({ children, pathname }: AuthContainerProps & { pathname: string }) => {
  const normalizedPath = pathname.replace(/^\/+/, "");
  const shouldHideTabs = [ROUTES.FORGOT_PASSWORD, ROUTES.RESET_PASSWORD, ROUTES.COMPANY_REGISTER].includes(
    normalizedPath as typeof ROUTES.FORGOT_PASSWORD
  );
  const splitContent = getSplitContent(pathname);

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100%",
        display: "flex",
      }}>
      <Box
        sx={{
          width: "100%",
          flex: 1,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "44% 56%" },
          minHeight: "100%",
        }}>
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            display: "flex",
            color: "common.white",
            px: { xs: 3, sm: 5, md: 6.5 },
            py: { xs: 4, md: 6 },
            background: "linear-gradient(180deg, #2A3170 0%, #2F52DF 68%, #3B5BF1 100%)",
          }}>
          <Box
            sx={{
              position: "absolute",
              top: { xs: -80, md: -60 },
              right: { xs: -120, md: -90 },
              width: { xs: 240, md: 420 },
              height: { xs: 240, md: 420 },
              borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.08)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: { xs: -110, md: -120 },
              left: { xs: -90, md: -110 },
              width: { xs: 240, md: 320 },
              height: { xs: 240, md: 320 },
              borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.08)",
            }}
          />

          <Stack
            spacing={{ xs: 3, md: 5 }}
            justifyContent='center'
            sx={{ position: "relative", zIndex: 1, height: "100%", width: "100%" }}>
            <Box
              component='img'
              src={LogoImg}
              alt='MyHRBox'
              sx={{
                width: { xs: 150, sm: 172 },
                height: "auto",
              }}
            />

            <Stack spacing={2.5}>
              <Typography
                sx={{
                  fontSize: { xs: 32, md: 48 },
                  lineHeight: { xs: 1.15, md: 1.08 },
                  fontWeight: 700,
                  color: "white",
                }}>
                {splitContent.title}
              </Typography>

              <Typography
                variant='body16rg'
                sx={{
                  color: "white",
                  fontSize: { xs: 18, md: 20 },
                  lineHeight: 1.65,
                  maxWidth: 500,
                }}>
                {splitContent.description}
              </Typography>
            </Stack>

            <Stack spacing={2}>
              {splitContent.features.map((feature) => (
                <Stack
                  key={feature.text}
                  direction='row'
                  spacing={2}
                  alignItems='center'>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: 2.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(255,255,255,0.12)",
                    }}>
                    {feature.icon}
                  </Box>

                  <Typography
                    variant='body16rg'
                    sx={{ color: "common.white", fontSize: { xs: 18, md: 20 }, lineHeight: 1.4 }}>
                    {feature.text}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            {splitContent.showMetrics && (
              <Stack
                direction='row'
                spacing={{ xs: 4, md: 8 }}
                sx={{
                  pt: 3,
                  mt: { xs: 1, md: "auto" },
                  borderTop: "1px solid rgba(255,255,255,0.18)",
                }}>
                <Box>
                  <Typography sx={{ fontSize: { xs: 32, md: 42 }, fontWeight: 700, lineHeight: 1, color: "white" }}>
                    89%
                  </Typography>
                  <Typography
                    variant='body14rg'
                    sx={{ color: "white", maxWidth: 180, mt: 1 }}>
                    совпадение оценок AI и эксперта
                  </Typography>
                </Box>

                <Box>
                  <Typography sx={{ fontSize: { xs: 32, md: 42 }, fontWeight: 700, lineHeight: 1, color: "white" }}>
                    x3-5
                  </Typography>
                  <Typography
                    variant='body14rg'
                    sx={{ color: "white", maxWidth: 180, mt: 1 }}>
                    пропускная способность
                  </Typography>
                </Box>
              </Stack>
            )}
          </Stack>
        </Box>

        <Box
          sx={{
            backgroundColor: "common.white",
            display: "flex",
            minHeight: "100%",
            justifyContent: "center",
            px: { xs: 3, sm: 5, md: 7 },
            py: { xs: 4, md: 6 },
          }}>
          <Stack
            spacing={4}
            sx={{
              width: "100%",
              maxWidth: 664,
              justifyContent: "center",
            }}>
            {!shouldHideTabs && <AuthTabs pathname={pathname} />}
            <Box>{children}</Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

const DefaultAuthContainer: FC<AuthContainerProps> = ({ children }) => (
  <Container
    disableGutters
    maxWidth={false}
    sx={{
      backgroundImage: `url(${BgImg2}), url(${BackgroundImg})`,
      backgroundPosition: "100% bottom, 42% 40%",
      backgroundSize: "40% 74%, 140% 387.792%",
      backgroundRepeat: "no-repeat",
      position: "relative",
      width: "100%",
      minHeight: "100%",
      py: 6,
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-start",
      "& > div": {
        zIndex: 1,
      },
    }}>
    <Box
      component='img'
      src={BgImg1}
      alt=''
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        height: "auto",
        width: "33vw",
        zIndex: 0,
      }}
    />
    {children}
  </Container>
);

const AuthContainer: FC<AuthContainerProps> = ({ children }) => {
  const location = useLocation();

  if (isSplitAuthRoute(location.pathname)) {
    return (
      <PublicThemeProvider>
        <AuthSplitContainer pathname={location.pathname}>{children}</AuthSplitContainer>
      </PublicThemeProvider>
    );
  }

  return (
    <PublicThemeProvider>
      <DefaultAuthContainer>{children}</DefaultAuthContainer>
    </PublicThemeProvider>
  );
};

export default AuthContainer;
